import {
  GaiaEventType,
  WebsocketSubscriptionTarget,
  type GaiaEvent,
  type ReviewProjectFileInDb,
  type ReviewProjectFilePageViewInDb,
  type ReviewProjectTagInDb,
} from '@/api';
import { getWsClient, type WebSocketEventHandler } from '@/services/WebSocketService';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { onBeforeUnmount, onMounted, watch } from 'vue';
import { logger } from '@/utils/logger';

export interface UseReviewProjectFileWebSocketOptions {
  /** File uids to keep subscribed (the current page). Reconciled live. */
  fileUids: () => string[];
  /** A `review_project_file_updated` arrived with the full updated file — patch it in place. */
  onFileUpdated: (file: ReviewProjectFileInDb) => void;
  /** A `review_project_file_updated` arrived with the file's new tag set. */
  onTagsUpdated: (fileUid: string, tags: ReviewProjectTagInDb[]) => void;
  /** A `review_project_file_seen_updated` arrived with the file's page views. */
  onSeenUpdated: (fileUid: string, pagesSeen: ReviewProjectFilePageViewInDb[]) => void;
  /** The event payload didn't carry usable file data — refetch instead of going stale. */
  onStale: () => void;
}

interface ObjectPayload {
  uid: string;
  data: unknown;
}

function eventObjectPayload(event: GaiaEvent): ObjectPayload | null {
  const data = event.data;
  if (typeof data !== 'object' || !('uid' in data)) {
    return null;
  }
  const uid = (data as { uid?: unknown }).uid;
  if (typeof uid !== 'string') {
    return null;
  }
  return { uid, data: (data as { data?: unknown }).data };
}

function isReviewProjectFile(value: unknown): value is ReviewProjectFileInDb {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { uid?: unknown }).uid === 'string' &&
    Array.isArray((value as { tags?: unknown }).tags)
  );
}

/** The backend's partial dumps: `{tags: [...]}` on updated, `{pages_seen: [...]}` on seen. */
function arrayField<T>(value: unknown, field: string): T[] | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const entries = (value as Record<string, unknown>)[field];
  return Array.isArray(entries) ? (entries as T[]) : null;
}

/**
 * Keep the visible review-project files in sync with the file events. The chat and
 * auto-tag flows change tags server-side (`review_project_file_updated`, payload
 * `{tags: [...]}`), and any viewer paging through a preview extends the seen pages
 * (`review_project_file_seen_updated`, payload `{pages_seen: [...]}`). Both carry the
 * changed field in full, so rows are patched in place without a refetch; `onStale` is
 * the escape hatch for payloads that don't parse. Subscriptions follow the current
 * page's file uids and are refcounted in the service, so the usable and unusable
 * tables can watch disjoint sets independently.
 */
export function useReviewProjectFileWebSocket(options: UseReviewProjectFileWebSocketOptions): void {
  const subs = new Map<string, () => void>();
  const disposers: (() => void)[] = [];

  function reconcile(desired: string[]): void {
    const wsClient = getWsClient();
    const desiredSet = new Set(desired.filter(Boolean));

    for (const [uid, dispose] of subs) {
      if (!desiredSet.has(uid)) {
        dispose();
        subs.delete(uid);
      }
    }
    for (const uid of desiredSet) {
      if (!subs.has(uid)) {
        subs.set(
          uid,
          wsClient.addSubscriptionUid(WebsocketSubscriptionTarget.REVIEW_PROJECT_FILE, uid)
        );
      }
    }
  }

  const handler: WebSocketEventHandler = event => {
    const payload = eventObjectPayload(event);
    if (!payload || !subs.has(payload.uid)) {
      return;
    }

    if (event.gaia_event_type === GaiaEventType.REVIEW_PROJECT_FILE_UPDATED) {
      const tags = arrayField<ReviewProjectTagInDb>(payload.data, 'tags');
      if (isReviewProjectFile(payload.data)) {
        options.onFileUpdated(payload.data);
      } else if (tags) {
        options.onTagsUpdated(payload.uid, tags);
      } else {
        options.onStale();
      }
      return;
    }

    if (event.gaia_event_type === GaiaEventType.REVIEW_PROJECT_FILE_SEEN_UPDATED) {
      const pagesSeen = arrayField<ReviewProjectFilePageViewInDb>(payload.data, 'pages_seen');
      if (pagesSeen) {
        options.onSeenUpdated(payload.uid, pagesSeen);
      } else {
        options.onStale();
      }
    }
  };

  onMounted(() => {
    if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
      logger.warn('[WebSocket] WebSocket is disabled via settings');
      return;
    }

    const wsClient = getWsClient();
    disposers.push(
      wsClient.registerHandler(WebsocketSubscriptionTarget.REVIEW_PROJECT_FILE, handler),
      watch(
        options.fileUids,
        uids => {
          reconcile(uids);
        },
        { immediate: true }
      )
    );
  });

  onBeforeUnmount(() => {
    for (const dispose of subs.values()) {
      dispose();
    }
    subs.clear();
    for (const dispose of disposers) {
      dispose();
    }
    disposers.length = 0;
  });
}
